import { isEqual } from 'lodash-es';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';
import { Prompt, useHistory } from 'react-router-dom';

import SelectItemModal from '@/ui/components/selectItemModal/SelectItemModal';
import { Button as AmbrosiaButton, Button, Modal } from '@payaca/ambrosia';
import BasicField from '@payaca/components/basicField/BasicField';
import TextareaField from '@payaca/components/textareaField/TextareaField';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';

import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import {
  LineItemGroup,
  LineItemGroupLineItem,
} from '@payaca/types/lineItemGroupTypes';

import {
  getIsRequiredFieldValidator,
  getLengthFieldValidator,
} from '@payaca/helpers/fieldValidationHelper';
import { currencyPrice } from '@payaca/helpers/financeHelper';

import { usePrevious } from '@/utils/customHooks';

import * as lineItemGroupActions from '@payaca/store/lineItemGroups/lineItemGroupsActions';
import * as lineItemV2Actions from '@payaca/store/lineItemsV2/lineItemsActions';

import { useSelector } from '@/api/state';
import {
  getLineItemGroup,
  getLineItemsByLineItemIds,
} from '@/utils/stateAccessors';
import CreateArea from '@payaca/components/clickableArea/CreateArea';
import ContentPanel from '@payaca/components/contentPanel/ContentPanel';
import { ErrorMessage } from '@payaca/components/feedbackMessage/FeedbackMessage';
import LabelValuePair from '@payaca/components/labelValuePair/LabelValuePair';
import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import { calculateLineItemGroupTotals } from '@payaca/helpers/lineItemGroupTotalsHelper';
import BodyWithSidePanelContentWrapper from '../bodyWithSidePanelContentWrapper/BodyWithSidePanelContentWrapper';
import LineItemGroupLineItemControl from '../lineItemGroupLineItemControl/LineItemGroupLineItemControl';
import './CreateEditLineItemGroupControl.sass';

interface IGroupItem {
  isMultipleChoice: boolean;
  isSelected: boolean;
  isOptional: boolean;
  [key: string]: any;
}

type Props = {
  lineItemGroupId: number;
};
const CreateEditLineItemControl: FunctionComponent<Props> = ({
  lineItemGroupId,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [showItemSearch, setShowItemSearch] = useState<boolean>(false);
  const lineItemGroup = useSelector((state) =>
    getLineItemGroup(state, lineItemGroupId)
  );
  const currentLineItemGroup = useSelector(
    (state) => state.lineItemGroups.lineItemGroups[lineItemGroupId]
  );
  const isUpdatingLineItemGroup = useSelector(
    (state) => state.lineItemGroups.isUpdatingLineItemGroup
  );
  const isArchivingLineItemGroup = useSelector(
    (state) => state.lineItemGroups.isArchivingLineItemGroup
  );
  const archiveLineItemGroupErrorMessage = useSelector(
    (state) => state.lineItemGroups.archiveLineItemGroupErrorMessage
  );
  const isArchiveLineItemGroupSuccessful = useSelector(
    (state) => state.lineItemGroups.isArchiveLineItemGroupSuccessful
  );
  const isUpdateLineItemGroupSuccessful = useSelector(
    (state) => state.lineItemGroups.isUpdateLineItemGroupSuccessful
  );
  const updateLineItemGroupErrorMessage = useSelector(
    (state) => state.lineItemGroups.updateLineItemGroupErrorMessage
  );
  const account = useSelector(
    (state: any) => state.users.myProfile.accounts[0]
  );

  const currentLineItemGroupEntity = useMemo(
    () => currentLineItemGroup?.entity || null,
    [currentLineItemGroup]
  );

  const previousIsUpdatingLineItemGroup: any = usePrevious(
    isUpdatingLineItemGroup
  );

  useEffect(() => {
    if (
      !updateLineItemGroupErrorMessage &&
      previousIsUpdatingLineItemGroup &&
      !isUpdatingLineItemGroup &&
      isUpdateLineItemGroupSuccessful
    ) {
      // item group has successfully saved
      history.push('/items/groups');
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'history'
  }, [
    previousIsUpdatingLineItemGroup,
    isUpdatingLineItemGroup,
    updateLineItemGroupErrorMessage,
    isUpdateLineItemGroupSuccessful,
  ]);

  const [showDeleteGroupModal, setShowDeleteGroupModal] =
    useState<boolean>(false);

  const isFetchingLineItemGroup = useMemo(
    () =>
      lineItemGroupId && currentLineItemGroup
        ? currentLineItemGroup.isFetching
        : true,
    [currentLineItemGroup, lineItemGroupId]
  );

  useEffect(() => {
    if (lineItemGroupId) {
      dispatch(lineItemGroupActions.requestGetLineItemGroup(lineItemGroupId));
      dispatch(
        lineItemV2Actions.requestGetLineItemsForLineItemGroup(lineItemGroupId)
      );
    }
    return () => {
      dispatch(lineItemGroupActions.clearUpdateLineItemGroup());
      dispatch(lineItemGroupActions.clearArchiveLineItemGroup());
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [lineItemGroupId]);

  const onSubmit = useCallback(
    (formState: { [key: string]: any }) => {
      if (!lineItemGroupId) {
        return;
      }
      dispatch(
        lineItemGroupActions.requestUpdateLineItemGroup(lineItemGroupId, {
          id: lineItemGroupId,
          description: formState.description,
          name: formState.name,
          lineItemRelations: formState.lineItemRelations,
        })
      );
    },
    [dispatch, lineItemGroupId]
  );

  const initialFormState = useMemo(() => {
    return {
      id: currentLineItemGroupEntity?.id,
      name: currentLineItemGroupEntity?.name,
      description: currentLineItemGroupEntity?.description,
      lineItemRelations: currentLineItemGroupEntity?.lineItemRelations || [],
    };
  }, [currentLineItemGroupEntity]);

  const fieldValidators = useMemo(() => {
    return {
      name: [
        getIsRequiredFieldValidator(),
        getLengthFieldValidator({ min: 0, max: 255 }),
      ],
    };
  }, []);

  const archiveLineItemGroup = useCallback(() => {
    dispatch(lineItemGroupActions.requestArchiveLineItemGroup(lineItemGroupId));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'lineItemGroupId'
  }, [dispatch]);

  useEffect(() => {
    if (isArchiveLineItemGroupSuccessful && !archiveLineItemGroupErrorMessage) {
      // successfully archive line item group - return to line item groups page
      history.push('/items/groups');
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'history'
  }, [isArchiveLineItemGroupSuccessful, archiveLineItemGroupErrorMessage]);

  const deselectMultiSelectItemsOnItemSelect = useCallback(
    (
      formState: { [key: string]: any },
      updateValue: { [key: string]: any },
      lineItemRelation: LineItemGroupLineItem,
      index: number
    ) => {
      const newUpdateValue = updateValue;

      if (
        (lineItemRelation.isMultipleChoice ||
          newUpdateValue[`lineItemRelations[${index}].isMultipleChoice`]) &&
        newUpdateValue[`lineItemRelations[${index}].isSelected`]
      ) {
        formState.lineItemRelations.forEach(
          (lineItemRelation: LineItemGroupLineItem, liIndex: number) => {
            if (liIndex !== index && lineItemRelation.isMultipleChoice) {
              newUpdateValue[`lineItemRelations[${liIndex}].isSelected`] =
                false;
            }
          }
        );
      }

      return newUpdateValue;
    },
    []
  );

  const setSelectedMultiSelectItemIfNone = useCallback(
    (formState: { [key: string]: any }) => {
      if (!formState?.lineItemRelations) return;

      const multiSelectItems = formState.lineItemRelations.filter(
        (x: LineItemGroupLineItem) => x.isMultipleChoice
      );

      if (!multiSelectItems.length) return;

      const hasSelectedMultiSelectItem = multiSelectItems.find(
        (x: LineItemGroupLineItem) => x.isMultipleChoice && x.isSelected
      );

      if (hasSelectedMultiSelectItem) return;

      const itemToSelect = multiSelectItems.at(-1);
      const itemToSelectIndex = formState.lineItemRelations.findIndex(
        (x: LineItemGroupLineItem) => x.id === itemToSelect.id
      );
      const updateValue = {
        [`lineItemRelations[${itemToSelectIndex}].isSelected`]: true,
      };
      return updateValue;
    },
    []
  );

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      const hasChangesMade = !isEqual(formState, initialFormState);

      const lineItems = useSelector((state) => {
        const lineItemIds =
          formState?.lineItemRelations?.map(
            (lineItemRelation: any) => lineItemRelation.lineItemId
          ) || [];
        return getLineItemsByLineItemIds(state, lineItemIds);
      });

      const isSaveDisabled =
        !isValid ||
        isFetchingLineItemGroup ||
        !formState.lineItemRelations?.length;

      const lineItemGroupTotals = useMemo(() => {
        if (!lineItemGroup) return;

        return calculateLineItemGroupTotals(
          formState as LineItemGroup,
          lineItems
        );
      }, [formState, lineItems]);

      const sidebarContent = useMemo(() => {
        return (
          <div className="sidebar-content-outer">
            <div className="save-button-container">
              <Button
                className="w-full"
                disabled={isSaveDisabled}
                onClick={() => !isUpdatingLineItemGroup && onSubmit(formState)}
                isProcessing={isUpdatingLineItemGroup}
                replaceContentWhenProcessing
              >
                Save
              </Button>
            </div>
            <div className="sidebar-content">
              <LabelValuePair
                label="Items added"
                value={formState?.lineItemRelations?.length || 0}
                suffixLabelWith={''}
              />
              {lineItemGroupTotals && (
                <>
                  <hr />
                  <LabelValuePair
                    label={`Total (inc tax)`}
                    value={currencyPrice(
                      lineItemGroupTotals.total,
                      account.region
                    )}
                    suffixLabelWith={''}
                  />
                  {/* TODO: Only show these if has optional/multi choice items */}
                  {lineItemGroupTotals.hasAnyNonRequiredItems && (
                    <>
                      <LabelValuePair
                        label={`Minimum total (inc tax)`}
                        value={currencyPrice(
                          lineItemGroupTotals.minTotal,
                          account.region
                        )}
                        suffixLabelWith={''}
                      />
                      <LabelValuePair
                        label={`Maximum total (inc tax)`}
                        value={currencyPrice(
                          lineItemGroupTotals.maxTotal,
                          account.region
                        )}
                        suffixLabelWith={''}
                      />
                    </>
                  )}
                </>
              )}
              <hr />
              <div className="delete-button-container">
                <Button
                  variant="link"
                  disabled={isFetchingLineItemGroup} // and does current line item group exist??
                  onClick={() => setShowDeleteGroupModal(true)}
                  isProcessing={isArchivingLineItemGroup}
                  replaceContentWhenProcessing
                >
                  Delete
                </Button>
              </div>
            </div>
            {(archiveLineItemGroupErrorMessage ||
              updateLineItemGroupErrorMessage) && (
              <ErrorMessage
                message={`
            Sorry, there was a problem
            ${
              archiveLineItemGroupErrorMessage ? 'deleting' : 'saving'
            } your item
            group. Please try again.`}
              />
            )}
          </div>
        );
      }, [
        formState,
        lineItemGroupTotals,
        isSaveDisabled,
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has an unnecessary dependency: isUpdatingLineItemGroup.
        isUpdatingLineItemGroup,
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has an unnecessary dependency: isFetchingLineItemGroup.
        isFetchingLineItemGroup,
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has an unnecessary dependency: isArchivingLineItemGroup.
        isArchivingLineItemGroup,
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has an unnecessary dependency: archiveLineItemGroupErrorMessage.
        archiveLineItemGroupErrorMessage,
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has an unnecessary dependency: updateLineItemGroupErrorMessage.
        updateLineItemGroupErrorMessage,
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has an unnecessary dependency: account.
        account,
      ]);

      return (
        <BodyWithSidePanelContentWrapper
          title={lineItemGroupId ? 'Edit item group' : 'Create new item group'}
          sidebarContent={sidebarContent}
        >
          <ContentPanel>
            {/* Item group name and description */}
            <div className="scrollable-content">
              <ValidatedFieldWrapper
                validationResult={validationState['name']}
                isTouched={touchedState['name'] || false}
              >
                <BasicField
                  label="Group name"
                  isRequired={true}
                  styleVariant={InputStyleVariant.OUTSIZE}
                  description="Your internal group name. This will not be seen by your customers."
                  value={formState.name || ''}
                  name="name"
                  onChange={onFieldChange}
                  onTouch={onFieldTouch}
                />
              </ValidatedFieldWrapper>
              <ValidatedFieldWrapper
                validationResult={validationState['description']}
                isTouched={touchedState['description'] || false}
              >
                <TextareaField
                  label="Description"
                  styleVariant={InputStyleVariant.OUTSIZE}
                  description="Description for your customers. This will be shown on your estimates, quotes and invoices."
                  value={formState.description || ''}
                  name="description"
                  onChange={onFieldChange}
                  onTouch={onFieldTouch}
                  rows={3}
                />
              </ValidatedFieldWrapper>

              {!!formState.lineItemRelations?.length && (
                <div className="line-item-controls-container">
                  {formState.lineItemRelations.map(
                    (
                      lineItemRelation: LineItemGroupLineItem,
                      index: number
                    ) => {
                      return (
                        <LineItemGroupLineItemControl
                          key={`line-item-group-line-item-control-${index}`}
                          lineItemGroupLineItem={lineItemRelation}
                          fieldNamePrefix={`lineItemRelations[${index}]`}
                          onTouch={onFieldTouch}
                          onChange={(value: { [key: string]: any }) => {
                            const updateObject =
                              deselectMultiSelectItemsOnItemSelect(
                                formState,
                                value,
                                lineItemRelation,
                                index
                              );
                            onFieldChange(updateObject);
                          }}
                          onDelete={() => {
                            formState.lineItemRelations.splice(index, 1);
                            onFieldChange({
                              lineItemRelations: formState.lineItemRelations,
                            });
                            const updateValue =
                              setSelectedMultiSelectItemIfNone(formState);
                            if (updateValue) {
                              onFieldChange(updateValue);
                            }
                          }}
                        />
                      );
                    }
                  )}
                </div>
              )}
            </div>
            {/* Add item to group button */}
            <div className="add-item-button-container">
              <CreateArea
                onClick={() => {
                  setShowItemSearch(true);
                }}
              >
                Add item to group
              </CreateArea>
            </div>

            {/* Select items modal */}
            <SelectItemModal
              isOpen={showItemSearch}
              onClose={() => setShowItemSearch(false)}
              onSelectItem={(itemId: number) => {
                onFieldChange({
                  lineItemRelations: [
                    ...formState.lineItemRelations,
                    {
                      lineItemId: itemId,
                      isMultipleChoice: false,
                      isSelected: false,
                      isOptional: false,
                      quantity: 1,
                    },
                  ],
                });
                // requestGetLineItem
                dispatch(lineItemV2Actions.requestGetLineItem(itemId));
                // close modal
                setShowItemSearch(false);
              }}
              disabledItemIds={[]}
            />
            <Prompt
              // only show prompt when there are changes made and hasnt finished updating or archiving
              when={
                hasChangesMade &&
                !isUpdateLineItemGroupSuccessful &&
                !isArchiveLineItemGroupSuccessful
              }
              message="There are unsaved changes on the page, are you sure you want to leave?"
            />
          </ContentPanel>
        </BodyWithSidePanelContentWrapper>
      );
    },
    [
      initialFormState,
      isFetchingLineItemGroup,
      isUpdatingLineItemGroup,
      isArchivingLineItemGroup,
      archiveLineItemGroupErrorMessage,
      updateLineItemGroupErrorMessage,
      account,
      lineItemGroupId,
      showItemSearch,
      isUpdateLineItemGroupSuccessful,
      isArchiveLineItemGroupSuccessful,
      setSelectedMultiSelectItemIfNone,
      lineItemGroup,
      onSubmit,
      deselectMultiSelectItemsOnItemSelect,
      dispatch,
    ]
  );

  return (
    <ResponsiveViewWrapper
      className="create-edit-line-item-group-control"
      downBreakpointSm={850}
    >
      <ValidatedForm
        fieldValidators={fieldValidators}
        initialFormState={initialFormState}
        renderFormContents={renderFormContents}
      />
      <Modal
        isOpen={showDeleteGroupModal}
        title="Delete item group"
        onClose={() => setShowDeleteGroupModal(false)}
        size="sm"
      >
        <Modal.Body>
          Are you sure you want to delete this item group?
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <AmbrosiaButton
              variant="secondary"
              onClick={() => setShowDeleteGroupModal(false)}
            >
              Cancel
            </AmbrosiaButton>
            <AmbrosiaButton
              onClick={() => {
                archiveLineItemGroup();
                setShowDeleteGroupModal(false);
              }}
            >
              Delete
            </AmbrosiaButton>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </ResponsiveViewWrapper>
  );
};

export default CreateEditLineItemControl;
